import { Injectable } from '@angular/core';
import { AlertService } from '../alert-service/alert.service';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, catchError, map, Observable, of } from 'rxjs';
import { environment } from '@environments/environment';
import { AlertPosition, AlertType } from '@domain/enums/alert/alert-type.enum';
import { CypherService } from '../cypher-service/cypher.service';
import { MXWidgetMessageBox } from '@domain/interfaces/mx/mx-widget-message-box.interface';
import { MXGateway } from '@domain/models/mx/gateway/mx.gateway';
import { MXUser } from '@domain/models/mx/mx-user.model';
import { MXWidget } from '@domain/models/mx/mx-widget.model';
import { MXBalances } from '@domain/models/mx/mx-balances.model';
import { MXOffersRewardsMessageBox } from '@domain/interfaces/mx/mx-offers-rewards-message-box.interface';

@Injectable({
  providedIn: 'root',
})
export class MXService extends MXGateway {
  private _mxUser: BehaviorSubject<MXUser> = new BehaviorSubject<MXUser>(this.getMXUserFromLocalStorage());
  private _mxWidgetMessageBox: BehaviorSubject<MXWidgetMessageBox> = new BehaviorSubject<MXWidgetMessageBox>({});
  private _mxMiniWidgetMessageBox: BehaviorSubject<MXWidgetMessageBox> = new BehaviorSubject<MXWidgetMessageBox>({});
  private _mxOffersRewardsMessageBox: BehaviorSubject<MXOffersRewardsMessageBox> = new BehaviorSubject<MXOffersRewardsMessageBox>({});

  constructor(
    private _alertService: AlertService,
    private _http: HttpClient,
    private _cypherService: CypherService
  ) {
    super();
    this.loadWidgetMessageBoxes();
    this.loadMiniWidgetMessageBoxes();
    this.loadOffersRewardsMessageBoxes();
  }

  public getMXUser(): Observable<MXUser> {
    this._mxUser.next(this.getMXUserFromLocalStorage());
    return this._mxUser.asObservable();
  }

  private getMXUserFromLocalStorage(): MXUser {
    const storedData = this._cypherService.decryptText(localStorage.getItem('mx')! ?? '');
    return storedData ? JSON.parse(storedData) : {};
  }

  public setMXUser(mxAccountsConnected: MXUser): void {
    this._mxUser.next(mxAccountsConnected);
    localStorage.setItem('mx', this._cypherService.encryptObject(mxAccountsConnected));
  }

  public validateMXUserAccounts(userId?: number): Observable<MXUser> {
    return this._http.get<MXUser>(`${environment.memberPortalServiceURL.replace('{{brandDomain}}', window.location.host)}/mx/accountsconnected?userId=${userId}`).pipe(
      map((mxAccountsConnected: MXUser) => {
        this.setMXUser(mxAccountsConnected);
        this._alertService.setLoading(false);
        return mxAccountsConnected;
      }),
      catchError((e) => {
        console.log(e);
        this._alertService.setLoading(false);
        this._alertService.showAlert({
          type: AlertType.Error,
          title: 'Error',
          description: e?.error?.Message,
          position: AlertPosition.TopCenter,
        });
        return of();
      })
    );
  }

  public getMXWidget(widgetType: string, mxUserId?: string): Observable<MXWidget> {
    this._alertService.setLoading(true);
    return this._http.get<MXWidget>(`${environment.memberPortalServiceURL.replace('{{brandDomain}}', window.location.host)}/mx/widget?mxUserId=${mxUserId}&widgetType=${widgetType}`).pipe(
      map((mxWidget: MXWidget) => {
        this._alertService.setLoading(false);
        return mxWidget;
      }),
      catchError((e) => {
        console.log(e);
        this._alertService.setLoading(false);
        this._alertService.showAlert({
          type: AlertType.Error,
          title: 'Error',
          description: e?.error?.Message,
          position: AlertPosition.TopCenter,
        });
        return of();
      })
    );
  }

  private getWidgetMessageBoxes(): Observable<Array<MXWidgetMessageBox>> {
    return this._http.get<{ MXWidgetMessageBoxes: Array<MXWidgetMessageBox> }>('assets/data/mx-widget-message-boxes.json').pipe(map((response) => response.MXWidgetMessageBoxes));
  }

  private getMiniWidgetMessageBoxes(): Observable<Array<MXWidgetMessageBox>> {
    return this._http
      .get<{ MXMiniWidgetMessageBoxes: Array<MXWidgetMessageBox> }>('assets/data/mx-mini-widget-message-boxes.json')
      .pipe(map((response) => response.MXMiniWidgetMessageBoxes));
  }

  private getOffersRewardsMessageBoxes(): Observable<Array<MXOffersRewardsMessageBox>> {
    return this._http
      .get<{ MXOffersRewardsMessageBoxes: Array<MXOffersRewardsMessageBox> }>('assets/data/mx-offers-rewards-message-boxes.json')
      .pipe(map((response) => response.MXOffersRewardsMessageBoxes));
  }

  public getWidgetMessageBox(): Observable<MXWidgetMessageBox> {
    return this._mxWidgetMessageBox.asObservable();
  }

  public getMiniWidgetMessageBox(): Observable<MXWidgetMessageBox> {
    return this._mxMiniWidgetMessageBox.asObservable();
  }

  public getOffersRewardsMessageBox(): Observable<MXOffersRewardsMessageBox> {
    return this._mxOffersRewardsMessageBox.asObservable();
  }

  loadWidgetMessageBoxes() {
    this.getWidgetMessageBoxes().subscribe({
      next: (mxWidgetMessageBoxes: Array<MXWidgetMessageBox>) => {
        let boxMessageId = parseInt(sessionStorage.getItem('mx-widget-box-message')! ?? '0');
        boxMessageId = boxMessageId > 3 ? 1 : boxMessageId + 1;
        let mxWidgetMessageBox = mxWidgetMessageBoxes.find((messageBox) => messageBox.id == boxMessageId)!;
        sessionStorage.setItem('mx-widget-box-message', (boxMessageId ?? -1)?.toString())!;
        this._mxWidgetMessageBox.next(mxWidgetMessageBox);
      },
      error: (error: any) => {},
    });
  }

  loadMiniWidgetMessageBoxes() {
    this.getMiniWidgetMessageBoxes().subscribe({
      next: (mxWidgetMessageBoxes: Array<MXWidgetMessageBox>) => {
        let boxMessageId = parseInt(sessionStorage.getItem('mx-mini-widget-box-message')! ?? '0');
        boxMessageId = boxMessageId > 2 ? 1 : boxMessageId + 1;
        let mxWidgetMessageBox = mxWidgetMessageBoxes.find((messageBox) => messageBox.id == boxMessageId)!;
        sessionStorage.setItem('mx-mini-widget-box-message', (boxMessageId ?? -1)?.toString())!;
        this._mxMiniWidgetMessageBox.next(mxWidgetMessageBox);
      },
      error: (error: any) => {},
    });
  }

  loadOffersRewardsMessageBoxes() {
    this.getOffersRewardsMessageBoxes().subscribe({
      next: (mxOffersRewardsMessageBoxes: Array<MXOffersRewardsMessageBox>) => {
        let boxMessageId = parseInt(sessionStorage.getItem('mx-offers-rewards-box-message')! ?? '0');
        boxMessageId = boxMessageId > 1 ? 1 : boxMessageId + 1;
        let mxOffersRewardsMessageBox = mxOffersRewardsMessageBoxes.find((messageBox) => messageBox.id == boxMessageId)!;
        sessionStorage.setItem('mx-offers-rewards-box-message', (boxMessageId ?? -1)?.toString())!;
        this._mxOffersRewardsMessageBox.next(mxOffersRewardsMessageBox);
      },
      error: (error: any) => {},
    });
  }

  calculateBalances(mxUserId?: string): Observable<MXBalances> {
    this._alertService.setLoading(true);
    return this._http.get<MXBalances>(`${environment.memberPortalServiceURL.replace('{{brandDomain}}', window.location.host)}/mx/balances?mxUserId=${mxUserId}`).pipe(
      map((mxBalances: MXBalances) => {
        this._alertService.setLoading(false);
        return mxBalances;
      }),
      catchError((e) => {
        console.log(e);
        this._alertService.setLoading(false);
        this._alertService.showAlert({
          type: AlertType.Error,
          title: 'Error',
          description: e?.error?.Message,
          position: AlertPosition.TopCenter,
        });
        return of();
      })
    );
  }
}
